import React, { useState, useEffect } from 'react';
import { Header, Button, Modal, TextField, DataGrid } from '../layout';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import { DocumentsSection } from '../features';

const parseJsonList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
};

const isFunzionario = (agent) => !agent.AreaManager || agent.AreaManager === '';
const isActive = (agent) => agent.is_active == 1 || agent.is_active === null || agent.is_active === undefined;

const AGENT_SEARCH_KEYS = ['Salesperson', 'Name', 'NameAreaManager'];
const AGENT_INITIAL_SORT = { key: 'Name', dir: 'asc' };

const AgentsManagement = () => {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentAgent, setCurrentAgent] = useState(null);
    const [activeTab, setActiveTab] = useState('info');
    const [allClients, setAllClients] = useState([]);
    const [extraClientSearch, setExtraClientSearch] = useState('');
    const [formData, setFormData] = useState({
        Salesperson: '',
        Name: '',
        AreaManager: '',
        NameAreaManager: '',
        password: '',
        email: '',
        role: 'agent',
        is_active: 1,
        linked_agents: [],
        extra_clients: []
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setDocumentTitle('Gestione Agenti');
    }, []);

    useEffect(() => {
        fetchAgents();
        api.getClients().then(res => {
            if (res.status === 'success' && res.clients) setAllClients(res.clients);
        }).catch(() => {});
    }, []);

    const fetchAgents = async () => {
        setIsLoading(true);
        try {
            const response = await api.getAgents();
            if (response.status === 'success' && response.agents) {
                setAgents(response.agents);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error fetching agents:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Impossibile caricare gli agenti'
            });

            // Fallback to sample data for development
            setAgents([
                {
                    Salesperson: 'AG001',
                    Name: 'Mario Rossi',
                    AreaManager: 'AM001',
                    NameAreaManager: 'Giuseppe Verdi'
                },
                {
                    Salesperson: 'AG002',
                    Name: 'Laura Bianchi',
                    AreaManager: 'AM001',
                    NameAreaManager: 'Giuseppe Verdi'
                },
                {
                    Salesperson: 'AG003',
                    Name: 'Paolo Neri',
                    AreaManager: 'AM002',
                    NameAreaManager: 'Anna Gialli'
                },
                {
                    Salesperson: 'AM001',
                    Name: 'Giuseppe Verdi',
                    AreaManager: '',
                    NameAreaManager: ''
                },
                {
                    Salesperson: 'AM002',
                    Name: 'Anna Gialli',
                    AreaManager: '',
                    NameAreaManager: ''
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.Salesperson) newErrors.Salesperson = 'Il codice agente è obbligatorio';
        if (!formData.Name) newErrors.Name = 'Il nome è obbligatorio';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddAgent = () => {
        setCurrentAgent(null);
        setFormData({
            Salesperson: '',
            Name: '',
            AreaManager: '',
            NameAreaManager: '',
            password: '',
            email: '',
            role: 'agent',
            is_active: 1,
            linked_agents: [],
            extra_clients: []
        });
        setExtraClientSearch('');
        setActiveTab('info');
        setModalOpen(true);
    };

    const handleEditAgent = (agent) => {
        setCurrentAgent(agent);
        // Parse linked_agents from JSON string if needed
        let linkedAgents = [];
        if (agent.linked_agents) {
            if (typeof agent.linked_agents === 'string') {
                try {
                    linkedAgents = JSON.parse(agent.linked_agents);
                } catch (e) {
                    linkedAgents = [];
                }
            } else if (Array.isArray(agent.linked_agents)) {
                linkedAgents = agent.linked_agents;
            }
        }
        let extraClients = [];
        if (agent.extra_clients) {
            if (typeof agent.extra_clients === 'string') {
                try { extraClients = JSON.parse(agent.extra_clients); } catch (e) { extraClients = []; }
            } else if (Array.isArray(agent.extra_clients)) {
                extraClients = agent.extra_clients;
            }
        }
        setFormData({
            Salesperson: agent.Salesperson,
            Name: agent.Name,
            AreaManager: agent.AreaManager || '',
            NameAreaManager: agent.NameAreaManager || '',
            password: '',
            email: agent.email || '',
            role: agent.role || 'agent',
            is_active: agent.is_active !== undefined ? agent.is_active : 1,
            hasPassword: agent.password ? true : false,
            linked_agents: linkedAgents,
            extra_clients: extraClients
        });
        setExtraClientSearch('');
        setActiveTab('info');
        setModalOpen(true);
    };

    const handleDeleteAgent = (agent) => {
        Swal.fire({
            title: 'Sei sicuro?',
            text: `Vuoi eliminare l'agente ${agent.Name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, elimina!',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    // Call API to delete agent
                    await api.deleteAgent(agent.Salesperson);

                    // Update local state
                    const updatedAgents = agents.filter(a => a.Salesperson !== agent.Salesperson);
                    setAgents(updatedAgents);

                    Swal.fire(
                        'Eliminato!',
                        'L\'agente è stato eliminato.',
                        'success'
                    );
                } catch (error) {
                    console.error('Error deleting agent:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Errore',
                        text: error.message || 'Impossibile eliminare l\'agente'
                    });
                }
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // If AreaManager is selected, find the name
        let nameAreaManager = '';
        if (formData.AreaManager) {
            const areaManager = agents.find(a => a.Salesperson === formData.AreaManager);
            if (areaManager) {
                nameAreaManager = areaManager.Name;
            }
        }

        // Prepare agent data - remove helper fields and empty password
        const { hasPassword, password, linked_agents, extra_clients, ...baseAgentData } = formData;
        const agentData = {
            ...baseAgentData,
            NameAreaManager: nameAreaManager,
            linked_agents: linked_agents && linked_agents.length > 0 ? JSON.stringify(linked_agents) : null,
            extra_clients: extra_clients && extra_clients.length > 0 ? JSON.stringify(extra_clients) : null
        };

        // Only include password if it's not empty (user wants to change it)
        if (password && password.trim() !== '') {
            agentData.password = password;
        }

        try {
            if (currentAgent) {
                // Update existing agent
                await api.updateAgent(currentAgent.Salesperson, agentData);

                // Update local state
                const updatedAgents = agents.map(agent =>
                    agent.Salesperson === currentAgent.Salesperson ? agentData : agent
                );
                setAgents(updatedAgents);
            } else {
                // Add new agent
                await api.createAgent(agentData);

                // Update local state
                setAgents([...agents, agentData]);
            }

            setModalOpen(false);

            Swal.fire({
                icon: 'success',
                title: currentAgent ? 'Agente aggiornato' : 'Agente aggiunto',
                showConfirmButton: false,
                timer: 1500
            });
        } catch (error) {
            console.error('Error saving agent:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: error.message || 'Impossibile salvare l\'agente'
            });
        }
    };

    // Get list of area managers for dropdown
    const areaManagers = agents.filter(agent =>
        !agent.AreaManager || agent.AreaManager === ''
    );

    const columns = [
        {
            key: 'Salesperson',
            header: 'Codice',
            sortValue: (a) => a.Salesperson,
            render: (a) => a.Salesperson
        },
        {
            key: 'Name',
            header: 'Nome',
            sortValue: (a) => a.Name,
            render: (a) => a.Name
        },
        {
            key: 'NameAreaManager',
            header: 'Funzionario',
            sortValue: (a) => a.NameAreaManager,
            render: (a) => (a.NameAreaManager ? a.NameAreaManager : '-')
        },
        {
            key: 'linked',
            header: 'Agenti Collegati',
            render: (agent) => {
                const linkedAgentsList = parseJsonList(agent.linked_agents);
                const extraClientsList = parseJsonList(agent.extra_clients);
                if (linkedAgentsList.length === 0 && extraClientsList.length === 0) {
                    return <span className="text-gray-400">-</span>;
                }
                return (
                    <div className="flex flex-wrap gap-1">
                        {linkedAgentsList.map(code => {
                            const linkedAgent = agents.find(a => a.Salesperson === code);
                            return (
                                <span
                                    key={code}
                                    className="badge bg-info text-white"
                                    title={linkedAgent ? linkedAgent.Name : code}
                                >
                                    {linkedAgent ? linkedAgent.Name : code}
                                </span>
                            );
                        })}
                        {extraClientsList.length > 0 && (
                            <span className="badge bg-success text-white" title={extraClientsList.join(', ')}>
                                {extraClientsList.length} clienti extra
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'type',
            header: 'Tipo',
            sortValue: (a) => (isFunzionario(a) ? 'Funzionario' : 'Agente'),
            render: (a) => (isFunzionario(a)
                ? <span className="badge bg-success">Funzionario</span>
                : <span className="badge bg-primary">Agente</span>)
        },
        {
            key: 'status',
            header: 'Stato',
            sortValue: (a) => (isActive(a) ? 'Attivo' : 'Disabilitato'),
            render: (a) => (isActive(a)
                ? <span className="badge bg-success">Attivo</span>
                : <span className="badge bg-secondary">Disabilitato</span>)
        },
        {
            key: 'actions',
            header: 'Azioni',
            align: 'center',
            render: (agent) => (
                <>
                    <button
                        onClick={() => handleEditAgent(agent)}
                        className="btn btn-sm btn-outline-primary me-2"
                    >
                        Modifica
                    </button>
                    <button
                        onClick={() => handleDeleteAgent(agent)}
                        className="btn btn-sm btn-outline-danger"
                    >
                        Elimina
                    </button>
                </>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            <Header
                title="Gestione Agenti"
                backText="Dashboard"
                backTo="/dashboard"
            />

            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">Elenco Agenti</h2>
                    <Button
                        onClick={handleAddAgent}
                        color="primary"
                        className="flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Nuovo Agente
                    </Button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <DataGrid
                            columns={columns}
                            rows={agents}
                            rowKey="Salesperson"
                            searchKeys={AGENT_SEARCH_KEYS}
                            searchPlaceholder="Cerca agente..."
                            initialSort={AGENT_INITIAL_SORT}
                        />
                    </div>
                )}
            </div>

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={currentAgent ? "Modifica Agente" : "Nuovo Agente"}
                size="lg"
            >
                {/* Tabs */}
                <div className="mb-4 border-b border-gray-200">
                    <ul className="flex flex-wrap -mb-px text-sm font-medium text-center" role="tablist">
                        <li className="mr-2" role="presentation">
                            <button
                                className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'info' ? 'border-red-500 text-red-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                                type="button"
                                role="tab"
                                onClick={() => setActiveTab('info')}
                            >
                                Informazioni Agente
                            </button>
                        </li>
                        {currentAgent && (
                            <li className="mr-2" role="presentation">
                                <button
                                    className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'documents' ? 'border-red-500 text-red-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                                    type="button"
                                    role="tab"
                                    onClick={() => setActiveTab('documents')}
                                >
                                    Documenti Riservati
                                </button>
                            </li>
                        )}
                    </ul>
                </div>

                {activeTab === 'info' && (
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4">
                        <TextField
                            id="Salesperson"
                            name="Salesperson"
                            label="Codice Agente"
                            value={formData.Salesperson}
                            onChange={handleInputChange}
                            error={errors.Salesperson}
                            required
                            disabled={!!currentAgent}
                        />
                        <TextField
                            id="Name"
                            name="Name"
                            label="Nome"
                            value={formData.Name}
                            onChange={handleInputChange}
                            error={errors.Name}
                            required
                        />

                        <div className="form-group">
                            <label htmlFor="AreaManager" className="block text-sm font-medium text-gray-700 mb-1">
                                Funzionario
                            </label>
                            <select
                                id="AreaManager"
                                name="AreaManager"
                                value={formData.AreaManager}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 bg-white/5 backdrop-blur-sm border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-red-500 focus:ring-red-500/50"
                            >
                                <option value="">Nessuno (è un funzionario)</option>
                                {areaManagers.map(manager => (
                                    <option key={manager.Salesperson} value={manager.Salesperson}>
                                        {manager.Name} ({manager.Salesperson})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <TextField
                            id="email"
                            name="email"
                            label="Email"
                            type="email"
                            value={formData.email || ''}
                            onChange={handleInputChange}
                            error={errors.email}
                            helperText="Email per comunicazioni"
                        />

                        <TextField
                            id="password"
                            name="password"
                            label="Password"
                            type="password"
                            value={formData.password || ''}
                            onChange={handleInputChange}
                            error={errors.password}
                            helperText={formData.hasPassword ? "Password già impostata. Inserisci una nuova password solo se vuoi modificarla." : "Password per accesso agente"}
                            placeholder={formData.hasPassword ? "••••••••" : ""}
                        />

                        <div className="form-group">
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                                Ruolo
                            </label>
                            <select
                                id="role"
                                name="role"
                                value={formData.role || 'agent'}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 bg-white/5 backdrop-blur-sm border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-red-500 focus:ring-red-500/50"
                            >
                                <option value="agent">Agente</option>
                                <option value="head_agent">Funzionario/Capo Agente</option>
                            </select>
                            <p className="mt-1 text-sm text-gray-500">
                                I funzionari possono vedere tutti i clienti dei loro agenti
                            </p>
                        </div>

                        <div className="form-group">
                            <label htmlFor="is_active" className="block text-sm font-medium text-gray-700 mb-1">
                                Stato Account
                            </label>
                            <select
                                id="is_active"
                                name="is_active"
                                value={formData.is_active !== undefined ? formData.is_active : 1}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 bg-white/5 backdrop-blur-sm border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-red-500 focus:ring-red-500/50"
                            >
                                <option value="1">Attivo</option>
                                <option value="0">Disabilitato</option>
                            </select>
                            <p className="mt-1 text-sm text-gray-500">
                                Se disabilitato, l'agente non potrà effettuare il login
                            </p>
                        </div>

                        <div className="form-group">
                            <label htmlFor="linked_agents" className="block text-sm font-medium text-gray-700 mb-1">
                                Agenti Collegati
                            </label>
                            <select
                                id="linked_agents"
                                name="linked_agents"
                                multiple
                                value={formData.linked_agents || []}
                                onChange={(e) => {
                                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                    setFormData(prev => ({
                                        ...prev,
                                        linked_agents: selectedOptions
                                    }));
                                }}
                                className="w-full px-4 py-2.5 bg-white/5 backdrop-blur-sm border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-red-500 focus:ring-red-500/50"
                                style={{ minHeight: '120px' }}
                            >
                                {agents
                                    .filter(agent => agent.Salesperson !== formData.Salesperson)
                                    .map(agent => (
                                        <option key={agent.Salesperson} value={agent.Salesperson}>
                                            {agent.Name} ({agent.Salesperson})
                                        </option>
                                    ))
                                }
                            </select>
                            <p className="mt-1 text-sm text-gray-500">
                                Seleziona uno o più agenti per vedere anche i loro clienti (CTRL+click per selezione multipla).
                                Gli agenti collegati non necessitano di password.
                            </p>
                            {formData.linked_agents && formData.linked_agents.length > 0 && (
                                <div className="mt-2">
                                    <p className="text-sm font-medium text-gray-700">Agenti selezionati:</p>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {formData.linked_agents.map(code => {
                                            const agent = agents.find(a => a.Salesperson === code);
                                            return (
                                                <span
                                                    key={code}
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                >
                                                    {agent ? agent.Name : code}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                linked_agents: prev.linked_agents.filter(c => c !== code)
                                                            }));
                                                        }}
                                                        className="ml-1 text-blue-600 hover:text-blue-800"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Extra Clients */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Clienti Extra Collegati
                            </label>
                            <input
                                type="text"
                                value={extraClientSearch}
                                onChange={(e) => setExtraClientSearch(e.target.value)}
                                placeholder="Cerca cliente per codice o ragione sociale..."
                                className="w-full px-4 py-2.5 bg-white/5 backdrop-blur-sm border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-red-500 focus:ring-red-500/50 mb-2"
                            />
                            {extraClientSearch.length >= 2 && (
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                                    {allClients
                                        .filter(c => {
                                            if ((formData.extra_clients || []).includes(c.CustSupp)) return false;
                                            const s = extraClientSearch.toLowerCase();
                                            return c.CustSupp.toLowerCase().includes(s) || (c.CompanyName || '').toLowerCase().includes(s);
                                        })
                                        .slice(0, 20)
                                        .map(c => (
                                            <button
                                                key={c.CustSupp}
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        extra_clients: [...(prev.extra_clients || []), c.CustSupp]
                                                    }));
                                                    setExtraClientSearch('');
                                                }}
                                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-50 last:border-0"
                                            >
                                                <span className="font-mono text-gray-500">{c.CustSupp}</span>
                                                <span className="ml-2 text-gray-800">{c.CompanyName}</span>
                                                {c.Salesperson && <span className="ml-2 text-xs text-gray-400">Ag: {c.NameSalesPerson || c.Salesperson}</span>}
                                            </button>
                                        ))
                                    }
                                    {allClients.filter(c => {
                                        if ((formData.extra_clients || []).includes(c.CustSupp)) return false;
                                        const s = extraClientSearch.toLowerCase();
                                        return c.CustSupp.toLowerCase().includes(s) || (c.CompanyName || '').toLowerCase().includes(s);
                                    }).length === 0 && (
                                        <p className="px-3 py-2 text-sm text-gray-400">Nessun cliente trovato</p>
                                    )}
                                </div>
                            )}
                            {formData.extra_clients && formData.extra_clients.length > 0 && (
                                <div className="mt-2">
                                    <p className="text-sm font-medium text-gray-700">Clienti selezionati ({formData.extra_clients.length}):</p>
                                    <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                                        {formData.extra_clients.map(code => {
                                            const client = allClients.find(c => c.CustSupp === code);
                                            return (
                                                <span
                                                    key={code}
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                >
                                                    {code} {client ? `- ${client.CompanyName}` : ''}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                extra_clients: prev.extra_clients.filter(c => c !== code)
                                                            }));
                                                        }}
                                                        className="ml-1 text-green-600 hover:text-green-800"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <p className="mt-1 text-sm text-gray-500">
                                Cerca e aggiungi clienti specifici (es. senza agente, interni, esteri) che questo agente potrà vedere.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setModalOpen(false)}
                        >
                            Annulla
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                        >
                            {currentAgent ? "Aggiorna" : "Salva"}
                        </Button>
                    </div>
                </form>
                )}

                {activeTab === 'documents' && currentAgent && (
                    <DocumentsSection
                        ownerType="agent"
                        ownerId={currentAgent.Salesperson}
                        ownerName={currentAgent.Name}
                    />
                )}
            </Modal>
        </div>
    );
};

export default AgentsManagement;